/**
 * Workflow API service for Page Studio integrations.
 *
 * Calls turbine (workflow engine) through stargate for workflow CRUD,
 * destinations, actions, config, and fields.
 *
 * Sources are fetched from dejavu's `/leads/v1/sources` endpoint (through stargate),
 * which aggregates all source types in a single response:
 *   - Builder4 pages: published pages and draft pages that contain at least one form
 *     (draft pages are included via the `/builder4/pages/workflow-sources` endpoint
 *     that dejavu calls internally — see ENG-19148 phase 2)
 *   - Builder3 pages and pop-ups: via foundry
 *   - Lead agents: via AiEngine
 *
 * Using this unified endpoint ensures pre-population of existing workflows works correctly
 * even when previously-saved sources reference Builder3 pages or other non-Builder4 kinds.
 */

import chevron from '@/services/chevron';
import type { PageListItem } from '@/store/pages/types';
import type {
  WorkflowSourceItem,
  WorkflowSourceList,
  WorkflowList,
  WorkflowResponse,
  WorkflowConfig,
  IntegrationList,
  ActionList,
  ActionConfigFieldsQuery,
  ActionConfigResponse,
  FieldsList,
} from '@/types/workflow';

const API_HOST = import.meta.env.VITE_API_STARGATE;
const MAIN_APP_HOST = import.meta.env.VITE_API_MAIN_APP;

// Turbine URIs

const WORKFLOWS_URI = '/turbine/v1/workflows';
const WORKFLOW_URI = '/turbine/v1/workflow';
const DESTINATIONS_URI = '/turbine/v1/destinations';
const DESTINATION_ACTIONS_URI = '/turbine/v1/destination/actions';
const ACTION_CONFIG_URI = '/turbine/v1/destination/action/config';
const FIELDS_URI = '/turbine/v1/fields';

// Sources URI (dejavu)

const SOURCES_URI = '/leads/v1/sources';

// Helpers

/**
 * Transform a Builder4 PageListItem into a WorkflowSourceItem in the same
 * shape returned by dejavu's /leads/v1/sources.
 */
export const pageToSourceOption = (page: PageListItem): WorkflowSourceItem => ({
  _meta: {
    id: page.id,
    created: page.createdAt,
    updated: page.updatedAt,
  },
  name: page.name || 'Untitled Page',
  type: 'Builder4Page',
});

// Sources

/**
 * Fetch all workflow sources for the current user via dejavu's /leads/v1/sources.
 *
 * Returns a combined list of Builder4 pages (published + draft with forms),
 * Builder3 pages, pop-ups, and lead agents. Draft Builder4 pages are included
 * because dejavu calls /builder4/pages/workflow-sources internally (ENG-19148).
 */
export async function getWorkflowSources(): Promise<WorkflowSourceList> {
  const url = `${API_HOST}${SOURCES_URI}`;
  return (await chevron.http({ url, method: 'GET' })) as WorkflowSourceList;
}

// Workflows

export async function getWorkflows(): Promise<WorkflowList> {
  const url = `${API_HOST}${WORKFLOWS_URI}`;
  return (await chevron.http({ url, method: 'GET' })) as WorkflowList;
}

export async function getWorkflow(workflowId: string): Promise<WorkflowResponse> {
  const url = `${API_HOST}${WORKFLOW_URI}/${workflowId}`;
  return (await chevron.http({ url, method: 'GET' })) as WorkflowResponse;
}

export async function createWorkflow(workflowData: WorkflowConfig): Promise<void> {
  const url = `${API_HOST}${WORKFLOWS_URI}`;
  await chevron.http({
    url,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: workflowData,
  });
}

export async function updateWorkflow(workflowData: WorkflowConfig): Promise<void> {
  const workflowId = workflowData._meta?.id;
  if (!workflowId) {
    throw new Error('Workflow ID is required for update.');
  }
  const url = `${API_HOST}${WORKFLOW_URI}/${workflowId}`;
  await chevron.http({
    url,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: workflowData,
  });
}

export async function deleteWorkflow(workflowId: string): Promise<void> {
  const url = `${API_HOST}${WORKFLOW_URI}/${workflowId}`;
  await chevron.http({ url, method: 'DELETE' });
}

// Paragon

interface ParagonTokenResponse {
  token: string;
}

export async function getParagonToken(): Promise<ParagonTokenResponse> {
  const url = `${MAIN_APP_HOST}/api/v1/paragon/token`;
  return (await chevron.http({ url, method: 'GET' })) as ParagonTokenResponse;
}

export async function reconnectParagonIntegration(credentialId: string): Promise<void> {
  const url = `${API_HOST}${DESTINATIONS_URI}/reconnect`;
  await chevron.http({
    url,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { credentialId },
  });
}

// Destinations

export async function getDestinations(): Promise<IntegrationList> {
  const url = `${API_HOST}${DESTINATIONS_URI}`;
  return (await chevron.http({ url, method: 'GET' })) as IntegrationList;
}

export async function getDestinationActions(integrationType: string): Promise<ActionList> {
  const url = `${API_HOST}${DESTINATION_ACTIONS_URI}?provider=${encodeURIComponent(integrationType)}`;
  return (await chevron.http({ url, method: 'GET' })) as ActionList;
}

export async function getActionConfigFields(
  query: ActionConfigFieldsQuery,
): Promise<ActionConfigResponse> {
  const url = `${API_HOST}${ACTION_CONFIG_URI}`;
  return (await chevron.http({
    url,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: query,
  })) as ActionConfigResponse;
}

// Fields

export async function getFields(
  provider: string,
  credentialId: string,
  contentUUIDs: string[],
  listId?: string,
): Promise<FieldsList> {
  const params = new URLSearchParams({
    provider,
    credentialId,
    contentUUIDs: contentUUIDs.join(','),
  });

  if (listId) {
    params.append('listId', listId);
  }

  const url = `${API_HOST}${FIELDS_URI}?${params.toString()}`;
  return (await chevron.http({ url, method: 'GET' })) as FieldsList;
}
